import {
  createVoteContest,
  VoteContest,
  updateVoteContest,
  getArtStyleList,
} from '@/api/voteActivity';
import {
  getActivityRuleOptions,
  type ActivityRuleOption,
} from '@/pages/member/newcomer-challenge/api';
import UploadImage from '@/components/UploadImage';
import ImageCropper from '@/components/ImageCropper';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  theme,
  Upload,
} from 'antd';
import dayjs from 'dayjs';
import { memo, useState, useEffect, useCallback } from 'react';
import QuillEditer from '@/components/QuillEditer/index';
import debounce from 'lodash/debounce';
import { UploadOutlined } from '@ant-design/icons';
import { ContestTypeEnum, contestTypeMap } from './type';
import styles from './VoteActivityDrawer.module.less';

const { RangePicker } = DatePicker;

// 权重设置组件
const WeightSettings = memo(() => {
  const calculateTotalWeight = (allValues: any) => {
    const keys = [
      'voteWeight',
      'likeWeight',
      'commentWeight',
      'collectWeight',
      'artlistWeight',
    ];
    return keys.reduce((acc, key) => acc + (Number(allValues[key]) || 0), 0);
  };

  return (
    <Form.Item
      noStyle
      shouldUpdate={(prev, cur) => {
        const keys = [
          'voteWeight',
          'likeWeight',
          'commentWeight',
          'collectWeight',
          'artlistWeight',
        ];
        return keys.some((k) => prev[k] !== cur[k]);
      }}
    >
      {({ getFieldsValue }) => {
        const total = calculateTotalWeight(getFieldsValue());
        return (
          <>
            <div style={{ marginBottom: 16 }}>
              <h3>权重设置</h3>
              <p style={{ color: total === 100 ? 'green' : 'red' }}>
                当前权重总和: {total}% {total === 100 ? '✓' : '(必须等于100%)'}
              </p>
            </div>
            {[
              'voteWeight',
              'likeWeight',
              'commentWeight',
              'collectWeight',
              'artlistWeight',
            ].map((field, idx) => (
              <Form.Item
                key={field}
                name={field}
                label={['投票', '点赞', '评论', '收藏', '艺术榜'][idx] + '权重'}
                rules={[
                  { required: true, message: `请输入${field}权重` },
                  { type: 'number', min: 0, max: 100, message: '范围0-100' },
                ]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            ))}
          </>
        );
      }}
    </Form.Item>
  );
});

WeightSettings.displayName = 'WeightSettings';

interface VoteActivityDrawerProps {
  visible: boolean;
  editData?: VoteContest;
  onSuccess: () => void;
  onClose: () => void;
  modelOptions: { label: string; value: string }[];
}

const VoteActivityDrawer: React.FC<VoteActivityDrawerProps> = ({
  visible,
  editData,
  onSuccess,
  onClose,
  modelOptions,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { token } = theme.useToken();
  const [styleOptions, setStyleOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [excelFile, setExcelFile] = useState<File | null>(null);
  const [ruleLang, setRuleLang] = useState<'ja' | 'en'>('ja'); // 规则描述语言：ja=日语，en=英语
  const [activityRuleOptions, setActivityRuleOptions] = useState<ActivityRuleOption[]>([]);
  const [loadingActivityRules, setLoadingActivityRules] = useState(false);

  const fetchStyles = useCallback(
    debounce((styleTypeId?: string) => {
      getArtStyleList(styleTypeId).then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setStyleOptions(
            res.data.map((item: any) => ({
              label: item.styleName,
              value: item.styleId,
            }))
          );
        }
      });
    }, 300),
    []
  );

  const checkWeightSum = (values: any) => {
    const keys = [
      'voteWeight',
      'likeWeight',
      'commentWeight',
      'collectWeight',
      'artlistWeight',
    ];
    return (
      keys.reduce((sum, key) => sum + (Number(values[key]) || 0), 0) === 100
    );
  };

  // 验证规则描述值的通用函数
  const validateRuleValue = useCallback((value: any) => {
    if (!value) return false;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      // Quill 编辑器空内容可能是 '<p><br></p>' 或 '<p></p>' 或只有空白字符
      if (trimmed === '' || trimmed === '<p><br></p>' || trimmed === '<p></p>' || trimmed.replace(/<[^>]*>/g, '').trim() === '') {
        return false;
      }
    }
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  }, []);

  // 滚动到指定标签的字段或第一个错误字段
  const scrollToFieldByLabel = useCallback((labelText?: string, delay: number = 150) => {
    setTimeout(() => {
      if (labelText) {
        // 优先查找包含指定标签文本的字段
        const labels = Array.from(document.querySelectorAll('.ant-form-item-label label'));
        const targetLabel = labels.find((label: any) => 
          label.textContent?.includes(labelText)
        );
        if (targetLabel) {
          const formItem = targetLabel.closest('.ant-form-item');
          if (formItem) {
            formItem.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
            return;
          }
        }
      }
      // 备用方案：查找第一个错误字段
      const firstErrorField = document.querySelector('.ant-form-item-has-error');
      if (firstErrorField) {
        firstErrorField.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, delay);
  }, []);

  // 处理规则描述验证错误
  const handleRuleDescriptionError = useCallback((
    fieldName: 'ruleVal' | 'ruleValEn',
    lang: 'ja' | 'en',
    labelText: string
  ) => {
    messageApi.error(`请填写${labelText}规则描述`);
    setRuleLang(lang);
    setTimeout(() => {
      form.validateFields([fieldName]).catch(() => {});
      scrollToFieldByLabel(labelText, 150);
    }, 150);
  }, [form, messageApi, scrollToFieldByLabel]);

  // 加载活动规则列表（用于规则说明下拉框）
  useEffect(() => {
    if (visible) {
      setLoadingActivityRules(true);
      getActivityRuleOptions()
        .then((res) => {
          if (res.status === 200 && res.data) {
            setActivityRuleOptions(res.data);
          }
        })
        .catch(() => {
          messageApi.error('获取活动规则列表失败');
        })
        .finally(() => {
          setLoadingActivityRules(false);
        });
    } else {
      setActivityRuleOptions([]);
    }
  }, [visible, messageApi]);

  useEffect(() => {
    if (visible && editData?.styleTypeId) {
      const styleIdStr = String(editData.styleId);
      getArtStyleList(editData.styleTypeId).then((res) => {
        if (res.success && Array.isArray(res.data)) {
          const styles = res.data.map((item: any) => ({
            label: item.styleName,
            value: String(item.styleId),
          }));
          setStyleOptions(styles);
          const matched = styles.find((s) => s.value === styleIdStr);
          if (matched?.value) {
            // 延迟设置，确保 Select 渲染后再填值
            setTimeout(() => {
              form.setFieldsValue({ styleId: styleIdStr });
            }, 0);
          }
        }
      });
    }
  }, [visible, editData?.styleTypeId, editData?.styleId, form]);

  const handleModelChange = useCallback(
    (changedValues: any) => {
      const changedModel = changedValues.styleTypeId;
      if (changedModel) {
        // 1. 清空当前风格字段
        form.setFieldsValue({ styleId: undefined });
        // 2. 拉取风格列表
        getArtStyleList(changedModel).then((res) => {
          if (res.success && Array.isArray(res.data)) {
            setStyleOptions(
              res.data.map((item: any) => ({
                label: item.styleName,
                value: item.styleId,
              }))
            );
          }
        });
      }
    },
    [form]
  );

  const onSubmit = async (values: any) => {
    if (!checkWeightSum(values)) {
      messageApi.error('权重总和必须等于100%');
      return;
    }
    if (!excelFile && !editData?.id) {
      messageApi.error('请上传提示词Excel文件');
      return;
    }
    // 验证规则描述：规则类型为文字时，需要验证日文和英文两个字段
    const ruleType = values.ruleType;
    
    // 规则类型为文字时，需要验证两个语言字段
    if (ruleType === 1) {
      const jaValid = validateRuleValue(values.ruleVal);
      const enValid = validateRuleValue(values.ruleValEn);
      
      if (!jaValid || !enValid) {
        // 优先显示日语错误，如果日语有错误
        if (!jaValid) {
          handleRuleDescriptionError('ruleVal', 'ja', '日语');
        } else if (!enValid) {
          // 如果日语已填写，但英语未填写，显示英语错误
          handleRuleDescriptionError('ruleValEn', 'en', '英语');
        }
        return;
      }
    } 
    else {
      // 规则类型为图片或链接时，只验证 ruleVal
      if (!validateRuleValue(values.ruleVal)) {
        messageApi.error(ruleType === 2 ? '请上传规则图片' : '请输入链接地址');
        form.validateFields(['ruleVal']).catch(() => {});
        return;
      }
    }
    const [submitBeginTime, submitEndTime] = values.submitTime;
    const [voteBeginTime, voteEndTime] = values.voteTime;
    let params = {
      ...values,
      submitBeginTime: submitBeginTime.valueOf(),
      submitEndTime: submitEndTime.valueOf(),
      voteBeginTime: voteBeginTime.valueOf(),
      voteEndTime: voteEndTime.valueOf(),
      id: editData?.id,
      type: values?.type || 1
    };
    delete params.submitTime;
    delete params.voteTime;

    params = Object.entries(params).reduce(
      (acc, [key, value]) => {
        if (typeof value === 'string') {
          acc[key] = value.trim();
        } else if (Array.isArray(value)) {
          acc[key] = value.map((item) =>
            typeof item === 'string' ? item.trim() : item
          );
        } else {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );

    const resp = editData
      ? await updateVoteContest(params, excelFile ?? null) // 编辑可不传
      : await createVoteContest(params, excelFile);

    if (resp.success) {
      messageApi.success(`${editData ? '更新' : '创建'}成功`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 500);
    } else {
      messageApi.error(resp.message || `${editData ? '更新' : '创建'}失败`);
    }
  };

  useEffect(() => {
    if (visible) {
      const initcontestType = ContestTypeEnum.Givs;
      const initWeight = {
        voteWeight: 0,
        likeWeight: 100,
        commentWeight: 0,
        collectWeight: 0,
        artlistWeight: 0,
      };
      const initial = editData
        ? {
            ...editData,
            contestType: editData.contestType ?? initcontestType,
            vipLimit: editData.vipLimit ?? 0,
            submitTime:
              editData.submitBeginTime && editData.submitEndTime
                ? [
                    dayjs(Number(editData.submitBeginTime)),
                    dayjs(Number(editData.submitEndTime)),
                  ]
                : undefined,
            voteTime:
              editData.voteBeginTime && editData.voteEndTime
                ? [
                    dayjs(Number(editData.voteBeginTime)),
                    dayjs(Number(editData.voteEndTime)),
                  ]
                : undefined,
            voteWeight: Number(editData.voteWeight) ?? initWeight.voteWeight,
            likeWeight: Number(editData.likeWeight) ?? initWeight.likeWeight,
            commentWeight:
              Number(editData.commentWeight) ?? initWeight.commentWeight,
            collectWeight:
              Number(editData.collectWeight) ?? initWeight.collectWeight,
            artlistWeight:
              Number(editData.artlistWeight) ?? initWeight.artlistWeight,
          }
        : {
            contestType: initcontestType,
            vipLimit: 0,
            ruleType: 1,
            voteWeight: initWeight.voteWeight,
            likeWeight: initWeight.likeWeight,
            commentWeight: initWeight.commentWeight,
            collectWeight: initWeight.collectWeight,
            artlistWeight: initWeight.artlistWeight,
          };
      form.setFieldsValue(initial);
      // 如果有 ruleValEn，确保在编辑模式下也能正确显示
      if (editData && (editData as any).ruleValEn) {
        form.setFieldsValue({ ruleValEn: (editData as any).ruleValEn });
      }
    } else {
      form.resetFields();
      setRuleLang('ja'); // 重置语言选择
    }
  }, [visible, editData, form]);

  const picList = [
    {
      id: 'contestImageJa',
      name: '推荐栏日语',
      width: 570,
      height: 840,
      isRequired: true,
      noCrop: true
    },
    {
      id: 'challengeImageJa',
      name: 'challenge-详情页日语',
      width: 786,
      height: 1704,
      isRequired: true,
      noCrop: false
    },
    {
      id: 'challengePostingJa',
      name: 'challenge-post日语',
      // 回退到 416×320：竖图源裁剪后强制放大到 1248 反而柔焦更糊，
      // 维持小尺寸输出 + 客户端 GPU 拉伸的老链路，视觉感受跟其他活动一致
      width: 416,
      height: 320,
      isRequired: false,
      noCrop: false
    },
    {
      id: 'challengeJudgingJa',
      name: 'challenge-judging日语',
      // 输出尺寸放大 2× 保留比例（666:312 = 1332:624），客户端展示更清晰
      width: 1332,
      height: 624,
      isRequired: false,
      noCrop: false
    },
    {
      id: 'contestImage',
      name: '推荐栏英语',
      width: 570,
      height: 840,
      isRequired: true,
      noCrop: true
    },
    {
      id: 'challengeImage',
      name: 'challenge-详情页英语',
      width: 786,
      height: 1704,
      isRequired: true,
      noCrop: false
    },
    {
      id: 'challengePosting',
      name: 'challenge-post英语',
      // 回退到 416×320：竖图源裁剪后强制放大到 1248 反而柔焦更糊，
      // 维持小尺寸输出 + 客户端 GPU 拉伸的老链路，视觉感受跟其他活动一致
      width: 416,
      height: 320,
      isRequired: false,
      noCrop: false
    },
    {
      id: 'challengeJudging',
      name: 'challenge-judging英语',
      // 输出尺寸放大 2× 保留比例（666:312 = 1332:624），客户端展示更清晰
      width: 1332,
      height: 624,
      isRequired: false,
      noCrop: false
    },
  ];

  const activityPicList = [
    { id: 'challengeCarouselEn',
      name: 'challenge-posting-轮播图英语',
      // 输出尺寸放大 4× 保留比例（327:193 = 1308:772），客户端展示更清晰
      width: 1308,
      height: 772,
      isRequired: false,
      noCrop: false
    },
    {
      id: 'challengeListEn',
      name: 'challenge-posting-列表英语',
      // 输出尺寸放大 5× 保留比例（158:118 = 790:590），客户端展示更清晰
      width: 790,
      height: 590,
      isRequired: false,
      noCrop: false
    },
    { id: 'challengeCarouselJa',
      name: 'challenge-posting-轮播图日语',
      // 输出尺寸放大 4× 保留比例（327:193 = 1308:772），客户端展示更清晰
      width: 1308,
      height: 772,
      isRequired: false,
      noCrop: false
    },
    { id: 'challengeListJa',
      name: 'challenge-posting-列表日语',
      // 输出尺寸放大 5× 保留比例（158:118 = 790:590），客户端展示更清晰
      width: 790,
      height: 590,
      isRequired: false,
      noCrop: false
    },
  ];

  return (
    <Drawer
      open={visible}
      width={1000}
      onClose={onClose}
      destroyOnClose
      maskClosable={false}
      className={styles.voteActivityDrawer}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{editData ? '编辑' : '创建'}投票活动</span>
          <div>
            <Button onClick={onClose} style={{ marginRight: 8 }}>
              取消
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                // 先进行基础表单验证
                try {
                  await form.validateFields();
                } catch (errorInfo: any) {
                  const errorFields = errorInfo?.errorFields || [];
                  if (errorFields.length > 0) {
                    messageApi.error('请填写所有必填项');
                    // 检查是否有规则描述的验证错误，切换到对应标签页
                    const ruleValError = errorFields.find((field: any) => {
                      const fieldName = field.name?.[0];
                      return fieldName === 'ruleVal' || fieldName === 'ruleValEn';
                    });
                    if (ruleValError) {
                      const fieldName = ruleValError.name?.[0];
                      setRuleLang(fieldName === 'ruleVal' ? 'ja' : 'en');
                    }
                    // 滚动到第一个错误字段
                    scrollToFieldByLabel(undefined, 150);
                  }
                  return;
                }

                // 基础验证通过后，进行自定义验证（规则描述的双语言验证）
                const values = form.getFieldsValue();
                const ruleType = values.ruleType;
                
                if (ruleType === 1) {
                  const jaValid = validateRuleValue(values.ruleVal);
                  const enValid = validateRuleValue(values.ruleValEn);
                  
                  if (!jaValid || !enValid) {
                    // 优先显示日语错误
                    if (!jaValid) {
                      handleRuleDescriptionError('ruleVal', 'ja', '日语');
                    } else if (!enValid) {
                      handleRuleDescriptionError('ruleValEn', 'en', '英语');
                    }
                    return;
                  }
                }

                // 所有验证通过后，提交表单
                form.submit();
              }}
            >
              确定
            </Button>
          </div>
        </div>
      }
    >
      {contextHolder}
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        onValuesChange={handleModelChange}
        style={{ width: 860 }}
      >
        <Form.Item
          name="contestType"
          label="活动类型"
          rules={[{ required: true, message: '请选择活动类型' }]}
        >
          <Radio.Group>
            {[...contestTypeMap.keys()].map((key) => {
              return (
                <Radio key={key} value={key}>
                  {contestTypeMap.get(key)}
                </Radio>
              );
            })}
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="vipLimit"
          label="是否为vip限定活动"
          rules={[{ required: true, message: '请选择' }]}
          initialValue={0}
        >
          <Radio.Group>
            <Radio value={0}>否</Radio>
            <Radio value={1}>是</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="title"
          label="主标题"
          rules={[{ required: true, message: '请输入主标题' }]}
        >
          <Input placeholder="请输入主标题" />
        </Form.Item>
        <Form.Item
          name="subtitle"
          label="副标题"
          rules={[{ required: true, message: '请输入副标题' }]}
        >
          <Input placeholder="请输入副标题" />
        </Form.Item>

       {/* <Form.Item
          label="活动图片(Please upload a WebP image under 1 MB..)"
          required
          className={styles.formItemControl}
        >
          {picList.map((value) => {
            return (
              <div
                key={`${value.id}-${value.name}`}
                className={styles.descText}
              >
                <Form.Item
                  name={value.id}
                  rules={value.isRequired ? [{ required: true, message: `请上传${value.name}` }] : []}
                >
                  <UploadImage
                    width={value.width}
                    height={value.height}
                    maxSize={1}
                    showSizeHint={false}
                    picDesc={value.name}
                    isLimitHeight={true}
                    showResolution={true}
                  />
                </Form.Item>
                <span style={{display: "flex", textAlign: "center"}}>
                  {`${value.name}`}
                  <br />
                  {`分辨率：${value.width}x${value.height}px`}
                </span>
              </div>
            );
          })}
        </Form.Item>  */}

         <Form.Item
          label="活动图片(Please upload an image under 1 MB, supported formats: PNG, JPEG, JPG, WebP)"
          required
          className={styles.formItemControl}
        >
          {picList.map((value) => {
            return (
              <div
                key={`${value.id}-${value.name}`}
                className={styles.descText}
              >
                <Form.Item
                  name={value.id}
                  rules={value.isRequired ? [{ required: true, message: `请上传${value.name}` }] : []}
                >
                  <ImageCropper
                    aspectRatio={16 / 9}
                    width={value.width}
                    height={value.height}
                    quality={1}
                    fixedCropBox={true}
                    cropBoxWidth={value.width}
                    cropBoxHeight={value.height}
                    // 新增功能
                    autoUpload={true}
                    showCroppedPreview={true}
                    picDesc={value.name}
                    // 新增配置
                    fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                    maxFileSizeMB={1}
                    enablePreview={true}
                    noCrop={value.noCrop}
                    enableModeSwitch={true}
                    defaultUploadMode="crop"
                    onUploadSuccess={(url) => {
                      console.log('上传成功，图片URL:', url);
                      // message.success('裁剪并上传成功！');
                      // 这里可以处理上传成功后的逻辑
                    }}
                    onUploadError={(error) => {
                      console.error('上传失败:', error);
                      message.error('上传失败');
                    }}
                    onCropComplete={(blob, url) => {
                      console.log('裁剪完成:', { blob, url });
                    }}
                    onCropCancel={() => {
                      console.log('取消裁剪');
                    }}
                  />
                </Form.Item>
                <span style={{ display: 'flex', textAlign: 'center' }}>
                  {`${value.name}`}
                  <br />
                  {`分辨率：${value.width}x${value.height}px`}
                </span>
              </div>
            );
          })}
        </Form.Item>

        <Form.Item
          label="新版本活动图片(Please upload an image under 1 MB, supported formats: PNG, JPEG, JPG, WebP)"
          className={styles.formItemControl}
          style={{ marginTop: 30 }}
        >
          {/* <Row gutter={[24, 24]} style={{ display: 'flex', flexWrap: 'wrap' }}> */}
            {activityPicList.map((value) => {
              return (
                // <Col
                //   key={`${value.id}-${value.name}`}
                //   style={{
                //     flex: '0 0 calc(20% - 20px)',
                //     maxWidth: 'calc(20% - 20px)',
                //     minWidth: 140,
                //     paddingLeft: 12,
                //     paddingRight: 12,
                //     boxSizing: 'border-box',
                //   }}
                // >
                  <div className={`${styles.descText} ${styles.activityImageCol}`}>
                    <Form.Item
                      name={value.id}
                      rules={value.isRequired ? [{ required: true, message: `请上传${value.name}` }] : []}
                    >
                      <ImageCropper
                        aspectRatio={value.width / value.height}
                        width={value.width}
                        height={value.height}
                        quality={1}
                        fixedCropBox={true}
                        cropBoxWidth={value.width}
                        cropBoxHeight={value.height}
                        autoUpload={true}
                        showCroppedPreview={true}
                        picDesc={value.name}
                        fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                        maxFileSizeMB={1}
                        enablePreview={true}
                        noCrop={value.noCrop}
                        enableModeSwitch={true}
                        defaultUploadMode="crop"
                        onUploadSuccess={(url) => {
                          console.log('上传成功，图片URL:', url);
                        }}
                        onUploadError={(error) => {
                          console.error('上传失败:', error);
                          message.error('上传失败');
                        }}
                        onCropComplete={(blob, url) => {
                          console.log('裁剪完成:', { blob, url });
                        }}
                        onCropCancel={() => {
                          console.log('取消裁剪');
                        }}
                      />
                    </Form.Item>
                    <span style={{ display: 'flex', textAlign: 'center' }}>
                      {`${value.name}`}
                      <br />
                      {`分辨率：${value.width}x${value.height}px`}
                    </span>
                  </div>
                // </Col>
              );
            })}
          {/* </Row> */}
        </Form.Item>

        <Form.Item
          name="submitTime"
          label="投稿时间（日本时间）"
          rules={[{ required: true, message: '请选择投稿时间范围' }]}
        >
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['开始时间', '结束时间']}
          />
        </Form.Item>
        <Form.Item
          name="voteTime"
          label="用户评审时间（日本时间）"
          rules={[{ required: true, message: '请选择用户评审时间范围' }]}
        >
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['开始时间', '结束时间']}
          />
        </Form.Item>
        <Form.Item
          name="eventDescription"
          label="活动简介"
          rules={[{ required: true, message: '请输入活动简介' }]}
        >
          <Input placeholder="请输入活动简介" />
        </Form.Item>
        <Form.Item
          name="prizeNum"
          label="奖励人数"
          rules={[
            { required: true, message: '请输入奖励人数' },
            { type: 'number', min: 1, max: 99999, message: '1~99999' },
          ]}
        >
          <InputNumber
            placeholder="请输入奖励人数"
            min={1}
            max={99999}
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Form.Item
          name="prizeQuota"
          label="每人奖励"
          initialValue={0}
          rules={[{ type: 'number', min: 0, max: 99999, message: '0~99999' }]}
        >
          <InputNumber min={0} max={99999} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="otherPrize" label="其他奖励">
          <Input placeholder="请输入其他奖励描述" />
        </Form.Item>
        <Form.Item name="type" label="APP内显示顺序">
          <InputNumber
            controls={false}
            min={1}
            max={1000}
            precision={0}
            placeholder="请输入1~1000的整数；数字越大在APP/contest列表越靠前，1000显示在最前"
            style={{ width: '100%' }}
          />
        </Form.Item>
        
         <Form.Item
          name="ruleType"
          label="规则类型"
          rules={[{ required: true, message: '请选择规则类型' }]}
        >
          <Select
            options={[
              { label: '文字', value: 1 },
              { label: '图片', value: 2 },
              { label: '链接', value: 3 },
            ]}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prev, next) => prev.ruleType !== next.ruleType}
        >
          {({ getFieldValue }) => {
            const ruleType = getFieldValue('ruleType');
            // 规则类型为文字时，显示中英文切换
            if (ruleType === 1) {
              const renderEditor = (fieldName: string, label: string, isVisible: boolean) => {
                return (
                  <div style={{ display: isVisible ? 'block' : 'none' }}>
                    <Form.Item
                      name={fieldName}
                      label={label}
                      rules={[
                        {
                          required: true,
                          message: `请输入${label}`,
                          validator: (_, value) => {
                            if (!value || (typeof value === 'string' && value.trim() === '') || (typeof value === 'string' && value === '<p><br></p>')) {
                              return Promise.reject(new Error(`请输入${label}`));
                            }
                            return Promise.resolve();
                          }
                        }
                      ]}
                    >
                      <QuillEditer
                        useApi={true}
                        uploadConfig={{
                          action: `${SUPP_ADMIN_BASE_URL}/supp-management/file/upload/resourcePositionImage`,
                        }}
                      />
                    </Form.Item>
                  </div>
                );
              };
              return (
                <div>
                  <div style={{ marginBottom: 12 }}>
                    <Radio.Group
                      value={ruleLang}
                      onChange={(e) => setRuleLang(e.target.value)}
                      buttonStyle="solid"
                    >
                      <Radio.Button value="ja">日语</Radio.Button>
                      <Radio.Button value="en">英语</Radio.Button>
                    </Radio.Group>
                  </div>
                  {renderEditor('ruleVal', '规则描述（日语）', ruleLang === 'ja')}
                  {renderEditor('ruleValEn', '规则描述（英语）', ruleLang === 'en')}
                </div>
              );
            }
            
            // 规则类型为图片或链接时，只显示一个字段
            return (
              <Form.Item
                name="ruleVal"
                label="规则描述"
                rules={[
                  { required: true, message: ruleType === 2 ? '请上传规则图片' : '请输入链接地址' },
                  ...(ruleType === 3 ? [{ type: 'url' as const, message: '请输入有效的链接地址' }] : [])
                ]}
              >
                {ruleType === 2 ? (
                  <UploadImage
                    width={800}
                    height={400}
                    maxSize={10}
                    showSizeHint={false}
                  />
                ) : ruleType === 3 ? (
                  <Input placeholder="请输入链接地址" type="url" />
                ) : null}
              </Form.Item>
            );
          }}
        </Form.Item> 

        {/* <Form.Item
          name="ruleConfigId"
          label="规则说明"
          rules={[{ required: true, message: '请选择规则说明' }]}
        >
          <Select 
            placeholder="从规则库中选择" 
            allowClear
            loading={loadingActivityRules}
            options={activityRuleOptions.map((item) => {
              const en = item.activityTopicEn || '';
              const ja = item.activityTopicJa || '';
              const label = en && ja ? `${en} / ${ja}` : (en || ja || '未命名规则');
              return {
                label,
                value: item.id,
              };
            })}
          />
        </Form.Item> */}
        <WeightSettings />
        <Form.Item
          name="styleTypeId"
          label="主题选择"
          rules={[{ required: true, message: '请选择主题' }]}
        >
          <Select
            placeholder="请选择主题"
            options={modelOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
          />
        </Form.Item>
        <Form.Item
          name="styleId"
          label="风格选择"
          rules={[{ required: true, message: '请选择风格' }]}
        >
          <Select
            allowClear
            placeholder="请选择风格"
            options={styleOptions}
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            optionFilterProp="label"
            disabled={!form.getFieldValue('styleTypeId')}
            onDropdownVisibleChange={(open) => {
              if (open) {
                const currentModel = form.getFieldValue('styleTypeId');
                if (currentModel) {
                  fetchStyles(currentModel, '');
                }
              }
            }}
            notFoundContent={null}
          />
        </Form.Item>
        <Form.Item
          label="提示词表单上传"
          required={!editData?.id}
          validateStatus={!excelFile && !editData?.id ? 'error' : undefined}
          help={!excelFile && !editData?.id ? '请上传Excel文件' : ''}
        >
          <Upload
            beforeUpload={(file) => {
              setExcelFile(file); // 设置文件状态
              return false; // 阻止自动上传
            }}
            fileList={excelFile ? [excelFile as any] : []}
            onRemove={() => setExcelFile(null)}
            maxCount={1}
            accept=".xlsx,.xls"
          >
            <Button icon={<UploadOutlined />}>EXCEL上传</Button>
          </Upload>
        </Form.Item>

      </Form>
    </Drawer>
  );
};

export default VoteActivityDrawer;
